import { useEffect } from 'react';

const SITE_ORIGIN = 'https://tweediastudio.com';

// Marks the per-route JSON-LD block so it can be recognised again after a
// prerendered snapshot baked it into the HTML. The static Organization/WebSite
// graph in public/index.html carries no marker and is never touched.
export const JSON_LD_MARKER = 'data-page-jsonld';

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  const created = !el;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  const prev = el.getAttribute('content');
  el.setAttribute('content', content);
  return () => {
    if (created) el.remove();
    else if (prev !== null) el.setAttribute('content', prev);
  };
}

export default function usePageMeta({ title, description, canonicalPath, jsonLd }) {
  const jsonLdString = jsonLd ? JSON.stringify(jsonLd) : null;

  useEffect(() => {
    const restores = [];
    const prevTitle = document.title;

    if (title) {
      document.title = title;
      restores.push(upsertMeta('property', 'og:title', title));
      restores.push(upsertMeta('name', 'twitter:title', title));
    }
    if (description) {
      restores.push(upsertMeta('name', 'description', description));
      restores.push(upsertMeta('property', 'og:description', description));
      restores.push(upsertMeta('name', 'twitter:description', description));
    }
    if (canonicalPath) {
      const url = SITE_ORIGIN + canonicalPath;
      restores.push(upsertMeta('property', 'og:url', url));
      let link = document.head.querySelector('link[rel="canonical"]');
      const created = !link;
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      const prevHref = link.getAttribute('href');
      link.setAttribute('href', url);
      restores.push(() => {
        if (created) link.remove();
        else if (prevHref) link.setAttribute('href', prevHref);
      });
    }

    // Reuse the block a prerendered snapshot already baked in instead of
    // appending a second one: the marketing routes ship as static HTML
    // (scripts/prerender.js), which captures this tag, so an unconditional
    // append would leave every prerendered page with two identical JSON-LD
    // blocks once the app boots.
    let script = document.head.querySelector(`script[${JSON_LD_MARKER}]`);
    if (jsonLdString) {
      if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute(JSON_LD_MARKER, '');
        document.head.appendChild(script);
      }
      script.text = jsonLdString;
    } else if (script) {
      // This route has no JSON-LD of its own, so a block left over from a
      // prerendered snapshot would describe the wrong page. Drop it.
      script.remove();
      script = null;
    }

    return () => {
      document.title = prevTitle;
      restores.forEach((restore) => restore());
      if (script) script.remove();
    };
  }, [title, description, canonicalPath, jsonLdString]);
}
